<template>
  <template v-if="isProductCard">
    <ul
      v-if="renderedColorOptions.length > 1"
      class="color-swatches"
      :class="[
        { 'color-swatches--has-plus': renderPlusIcon },
        swatchCountClass
      ]"
    >
      <li class="color-swatches__swatches-wrapper">
        <div class="color-swatches__pagination-bars">
          <template v-for="(product, productIndex) of colorOptions">
            <div
              v-for="(_, colorIndex) of product.colorCodes"
              :key="`${productIndex}-${colorIndex}`"
              class="color-swatches__swatch-container"
            >
              <color-swatch
                v-if="!disabledCodes.includes(product.colorCodes[colorIndex])"
                :active-product-handle="productHandle"
                :color-code="product.colorCodes[colorIndex]"
                :color-label="product.colorLabels[colorIndex]"
                :color-value="product.values[colorIndex]"
                :featured-image-src="featuredImageSrc"
                :is-product-card="isProductCard"
                :namespace="namespace"
                :product-handle="product.handle"
                :variants="product.variants"
                @update-active-swatch="handleActiveSwatch"
              />
            </div>
          </template>
        </div>
      </li>

      <li
        v-if="renderPlusIcon"
        class="color-swatches__plus-container"
      >
        <a
          class="color-swatches__plus"
          :href="productUrl"
        >
          <span
            class="visually-hidden"
            v-text="$string('product.card.more_colors')"
          />

          <span class="color-swatches__plus-text">+{{ plusIconCount }}</span>
        </a>
      </li>
    </ul>
  </template>

  <div
    v-else-if="isLineItem"
    class="color-swatches"
  >
    <color-swatch
      v-for="(_, index) of renderedColorOptions"
      :key="index"
      :is-active="isActive"
      :color-code="colorOptions.colorCodes[index]"
      :color-label="colorOptions.colorLabels[index]"
      :color-value="colorOptions.values[index]"
      :featured-image-src="featuredImageSrc"
      :input-name="inputName"
      :namespace="namespace"
      :product-handle="productHandle"
      :variants="variants"
      :set-initial-swatch="setInitialSwatch"
      :is-line-item="isLineItem"
      @update-active-swatch="handleActiveSwatch"
    />
  </div>

  <div
    v-else
    class="color-swatches"
  >
    <template v-for="(product, productIndex) of colorOptions">
      <template v-for="(_, colorIndex) of product.colorCodes">
        <color-swatch
          v-if="!disabledCodes.includes(product.colorCodes[colorIndex])"
          :key="`${productIndex}-${colorIndex}`"
          :is-active="isActive"
          :active-product-handle="productHandle"
          :color-code="product.colorCodes[colorIndex]"
          :color-label="product.colorLabels[colorIndex]"
          :color-value="product.values[colorIndex]"
          :featured-image-src="featuredImageSrc"
          :input-name="inputName"
          :namespace="namespace"
          :product-handle="product.handle"
          :variants="product.variants"
          :set-initial-swatch="setInitialSwatch"
          :is-line-item="isLineItem"
          @update-active-swatch="handleActiveSwatch"
        />
      </template>
    </template>
  </div>
</template>

<script>

/**
 * Vue: Color swatches (color-swatches)
 * -----------------------------------------------------------------------------
 * Snippet to handle color swatches for products.
 *
 * @param {Object} colorOptions - Object with all color data.
 * @param {Array} disabledCodes - Array of disabled color codes.
 * @param {String} featuredImageSrc - Featured image source URL.
 * @param {String} [inputName] - Input name (PDP).
 * @param {Boolean} [isProductCard] - If swatches are imported by product card.
 * @param {String} namespace - Color swatch namespace.
 * @param {String} productHandle - Product handle for current product.
 * @param {String} productUrl - Product URL.
 * @param {Boolean} renderPlusIcon - Whether to render plus icon.
 * @param {Array} variants - Product variants array.
 * @param {Array} isActive - If dropdown in cart is active.
 *
 * @emits updateActiveSwatch - Emit active color code.
 *
 */
import ColorSwatch from './color-swatch'

export default {
  name: 'ColorSwatches',

  components: {
    ColorSwatch,
  },

  props: {
    colorOptions: {
      type: [Boolean, Object, Array],
      default: false,
      required: true,
    },
    disabledCodes: {
      type: Array,
      default: () => [],
    },
    featuredImageSrc: {
      type: String,
      default: '',
      required: true,
    },
    inputName: {
      type: String,
      default: '',
    },
    isProductCard: {
      type: Boolean,
      default: false,
    },
    namespace: {
      type: String,
      default: '',
      required: true,
    },
    productHandle: {
      type: String,
      default: '',
      required: true,
    },
    productUrl: {
      type: String,
      default: '',
    },
    renderPlusIcon: {
      type: Boolean,
      default: false,
    },
    plusIconCount: {
      type: Number,
      default: 0,
    },
    setInitialSwatch: {
      type: Boolean,
      default: true,
    },
    isLineItem: {
      type: Boolean,
      default: false,
    },
    variants: {
      type: [Boolean, Array],
      default: false,
      required: true,
    },
    isActive: {
      type: Boolean,
      default: false,
      required: false,
    },
  },

  emits: {
    'update-active-swatch': (payload) => typeof payload === 'object',
  },

  computed: {

    /**
     * Determine if swatches should be stacked vertically
     * @returns {Boolean}
     */
    shouldStackSwatches() {
      if (!this.isProductCard || !Array.isArray(this.colorOptions)) {
        return false
      }

      let totalColors = 0

      this.colorOptions.forEach(product => {
        if (product?.colorCodes) {
          const filteredColors = product.colorCodes.filter(
            code => !this.disabledCodes.includes(code),
          )
          totalColors += filteredColors.length
        }
      })

      // Stack if there are 2 or 3 colors (without plus icon)
      return totalColors >= 2 && totalColors <= 3 && !this.renderPlusIcon
    },

    /**
     * Compute how many color swatches to display. Max of 3 on product cards.
     * - Only render color codes if they're not disabled via product tag.
     * @returns {Array}
     */
    renderedColorOptions() {
      if (this.isLineItem) {
        const filteredColorCodes = this.colorOptions.colorCodes.filter(colorCode => {
          return !this.disabledCodes.includes(colorCode)
        })

        return filteredColorCodes

      }

      const renderedColorOptions = []

      if (!Array.isArray(this.colorOptions)) {
        return renderedColorOptions
      }

      this.colorOptions.forEach(product => {
        if (product?.colorCodes) {
          product.colorCodes.forEach(colorCode => {
            if (!this.disabledCodes.includes(colorCode)) {
              renderedColorOptions.push(colorCode)
            }
          })
        }
      })

      return renderedColorOptions
    },
    swatchCountClass() {
      if (!this.isProductCard || !Array.isArray(this.colorOptions)) {
        return ''
      }

      const totalSwatches = this.renderedColorOptions.length

      if (totalSwatches === 1) {
        return 'color-swatches--one-swatch'
      }
      if (totalSwatches === 2) {
        return 'color-swatches--two-swatches'
      }
      if (totalSwatches === 3) {
        return 'color-swatches--three-swatches'
      }

      return ''
    },
  },

  methods: {

    /**
    * Handle events when the active swatch is updated.
    * @param {Object} payload - Data from emitter.
    */
    handleActiveSwatch(payload) {
      if (this.isProductCard) {
        this.$emit('update-active-swatch', payload)
        return
      }

      if (payload?.handle && payload.handle !== this.productHandle) {
        cnvs.EventBus.emit('update-product', payload)
        return
      }

      this.$emit('update-active-swatch', payload)
    },
  },
}
</script>

<style lang="scss">
  @import './color-swatches';
</style>
